import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Bell, Plus, Send, Edit, Trash2, Calendar, Users, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';

const NotificationsManagementPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNotif, setEditingNotif] = useState(null);
  const [pushStats, setPushStats] = useState({ total_subscribers: 0, by_role: {} });
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    notification_type: 'info',
    target_roles: [],
    target_regions: [],
    target_users: [],
    scheduled_date: ''
  });

  const notificationTypes = [
    { value: 'info', label: 'Information', color: 'bg-blue-100 text-blue-800' },
    { value: 'warning', label: 'Avertissement', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'success', label: 'Succès', color: 'bg-green-100 text-green-800' },
    { value: 'error', label: 'Erreur', color: 'bg-red-100 text-red-800' }
  ];

  const roles = [
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'regional', label: 'Manager Régional' },
    { value: 'ophthalmologist', label: 'Ophtalmologiste' },
    { value: 'intern', label: 'Interne' }
  ];

  const statusBadges = {
    draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-800' },
    scheduled: { label: 'Programmée', color: 'bg-purple-100 text-purple-800' },
    sent: { label: 'Envoyée', color: 'bg-green-100 text-green-800' },
    cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-800' }
  };

  useEffect(() => {
    fetchNotifications();
    fetchPushStats();
  }, []);

  const fetchPushStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setPushStats(await response.json());
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.message) {
      toast.error('Titre et message obligatoires');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingNotif
        ? `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${editingNotif.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/notifications`;
      
      const method = editingNotif ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingNotif ? 'Notification modifiée' : 'Notification créée');
        setModalOpen(false);
        resetForm();
        fetchNotifications();
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleSendNow = async (notifId) => {
    if (!window.confirm('Envoyer cette notification push a tous les membres cibles ?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notifId}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const pr = data.push_result || {};
        if (pr.total > 0) {
          toast.success(`Push envoyé a ${pr.sent}/${pr.total} membre(s)`);
        } else {
          toast.success('Notification marquee comme envoyée (aucun abonne push)');
        }
        fetchNotifications();
        fetchPushStats();
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDelete = async (notifId) => {
    if (!window.confirm('Supprimer cette notification ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notifId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Notification supprimée');
        fetchNotifications();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditModal = (notif) => {
    setEditingNotif(notif);
    setFormData({
      title: notif.title,
      message: notif.message,
      notification_type: notif.notification_type,
      target_roles: notif.target_roles || [],
      target_regions: notif.target_regions || [],
      target_users: notif.target_users || [],
      scheduled_date: notif.scheduled_date || ''
    });
    setModalOpen(true);
  };

  const resetForm = () => {
    setEditingNotif(null);
    setFormData({
      title: '',
      message: '',
      notification_type: 'info',
      target_roles: [],
      target_regions: [],
      target_users: [],
      scheduled_date: ''
    });
  };

  const toggleRole = (role) => {
    setFormData(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
              <Bell className="text-[#1e3a5f]" size={32} />
              Gestion des Notifications
            </h1>
            <p className="text-gray-600 mt-1">Créez et envoyéz des notifications push aux membres</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setModalOpen(true);
            }}
            className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white"
            data-testid="new-notification-btn"
          >
            <Plus size={18} className="mr-2" />
            Nouvelle Notification
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm" data-testid="push-stats-total">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Bell className="text-white" size={16} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{pushStats.total_subscribers}</p>
                <p className="text-[11px] text-gray-500">Abonnes push</p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 text-blue-800">
            <div className="text-2xl font-bold">{notifications.filter(n => n.status === 'draft').length}</div>
            <div className="text-sm opacity-90">Brouillons</div>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 text-emerald-800">
            <div className="text-2xl font-bold">{notifications.filter(n => n.status === 'sent').length}</div>
            <div className="text-sm opacity-90">Envoyées</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 text-gray-800">
            <div className="text-2xl font-bold">{notifications.length}</div>
            <div className="text-sm opacity-90">Total</div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {notifications.length === 0 ? (
            <div className="text-center py-16">
              <Bell className="mx-auto text-gray-400 mb-4" size={64} />
              <h3 className="text-xl font-bold text-gray-600 mb-2">Aucune notification</h3>
              <p className="text-gray-500">Créez votre première notification</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {notifications.map((notif) => {
                const type = notificationTypes.find(t => t.value === notif.notification_type);
                const status = statusBadges[notif.status] || statusBadges.draft;
                
                return (
                  <div key={notif.id} className="p-5 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{notif.title}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${type?.color}`}>
                            {type?.label}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.color}`}>
                            {status.label}
                          </span>
                        </div>
                        
                        <p className="text-gray-700 mb-3">{notif.message}</p>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          {(notif.target_roles || []).length > 0 && (
                            <div className="flex items-center gap-1">
                              <Users size={14} />
                              <span>{(notif.target_roles || []).length} rôle(s)</span>
                            </div>
                          )}
                          {(notif.target_regions || []).length > 0 && (
                            <div className="flex items-center gap-1">
                              <MapPin size={14} />
                              <span>{(notif.target_regions || []).length} région(s)</span>
                            </div>
                          )}
                          {notif.scheduled_date && (
                            <div className="flex items-center gap-1">
                              <Calendar size={14} />
                              <span>{new Date(notif.scheduled_date).toLocaleString('fr-FR')}</span>
                            </div>
                          )}
                          {notif.sent_date && (
                            <span className="text-green-600 font-medium">
                              Envoyée le {new Date(notif.sent_date).toLocaleString('fr-FR')}
                            </span>
                          )}
                          {notif.push_sent !== undefined && notif.status === 'sent' && (
                            <span className="text-blue-600 font-medium flex items-center gap-1" data-testid={`push-stat-${notif.id}`}>
                              <Bell size={12} /> Push: {notif.push_sent}/{notif.push_total} delivre(s)
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        {(notif.status === 'draft' || notif.status === 'scheduled') && (
                          <Button
                            size="sm"
                            onClick={() => handleSendNow(notif.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Send size={16} className="mr-1" />
                            Envoyer
                          </Button>
                        )}
                        {notif.status !== 'sent' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditModal(notif)}
                          >
                            <Edit size={16} />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(notif.id)}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingNotif ? 'Modifier la notification' : 'Nouvelle notification'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Titre</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Titre de la notification"
                  required
                />
              </div>

              <div>
                <Label>Message</Label>
                <RichTextEditor
                  value={formData.message}
                  onChange={val => setFormData({ ...formData, message: val })}
                  placeholder="Contenu de la notification"
                  height="150px"
                />
              </div>

              <div>
                <Label>Type de notification</Label>
                <select
                  value={formData.notification_type}
                  onChange={(e) => setFormData({ ...formData, notification_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {notificationTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Cibler des rôles spécifiques (optionnel)</Label>
                <p className="text-xs text-gray-600 mb-2">Laisser vide pour tous les utilisateurs</p>
                <div className="space-y-2">
                  {roles.map(role => (
                    <label key={role.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.target_roles.includes(role.value)}
                        onChange={() => toggleRole(role.value)}
                        className="w-4 h-4 text-[#1e3a5f]"
                      />
                      <span className="text-sm">{role.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label>Programmer l&apos;envoi (optionnel)</Label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                />
                <p className="text-xs text-gray-600 mt-1">Laisser vide pour un envoi immédiat</p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-[#1e3a5f] hover:bg-[#162d4a] text-white">
                  {editingNotif ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default NotificationsManagementPage;
